import { useRef, useState, type FormEvent } from "react";
import type { NatwestApplicationWorkspace as Model } from "@myfm/domain";
import { formatDateOnly, formatDateTime } from "../dates";
import { useRecordDraft } from "../ui/RecordDraftBoundary";
import { confirmDiscard } from "../ui/confirm-action";
import type { NatwestCommand } from "./NatwestApplication";

export function NatwestPreparation({
  model: m,
  command,
  onClose,
}: {
  model: Model;
  command: NatwestCommand;
  onClose: () => void;
}) {
  const sourceHash = useRef(m.sourceHash);
  const [mapping, setMapping] = useState<Record<string, string>>({}),
    [supporting, setSupporting] = useState<string[]>([]),
    [reference, setReference] = useState(m.customerReference),
    [referenceReason, setReferenceReason] = useState("");
  const [formId, setFormId] = useState(
      m.formDownloads.find((f) => f.fresh)?.id ?? "",
    ),
    [notes, setNotes] = useState(""),
    [replace, setReplace] = useState(""),
    [revision, setRevision] = useState("");
  const [checks, setChecks] = useState<Record<string, boolean>>({}),
    [dirty, setDirty] = useState(false);
  useRecordDraft(dirty);
  const checksText = {
    confirmExistingAccountsReviewed:
      "I checked existing NatWest accounts and pending applications, and am applying only for the accounts still needed.",
    confirmCurrentFormRoute:
      "I used the retained current form and checked the S42 account route and declarations.",
    confirmAdobeSavedAndReopened:
      "I completed the form in Adobe, saved it and reopened it to check the completed content.",
    confirmNamesAndDeclarations:
      "I checked signatory details, actual dates, account names and declarations on every completed form.",
  };
  function change(fn: () => void) {
    setDirty(true);
    fn();
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    const forms = new Map<string, string[]>();
    for (const [id, versionId] of Object.entries(mapping))
      if (versionId)
        forms.set(versionId, [...(forms.get(versionId) ?? []), id]);
    const ok = await command("prepare", {
      sourceHash: sourceHash.current,
      formDownloadId: formId,
      forms: [...forms].map(([versionId, requirementIds]) => ({
        versionId,
        requirementIds,
      })),
      supportingVersionIds: supporting.filter((id) => !forms.has(id)),
      customerReference: reference,
      customerReferenceChangeReason: referenceReason,
      notes,
      replacesVersionId: replace || null,
      revisionReason: revision,
      ...checks,
    });
    if (ok) {
      setDirty(false);
      onClose();
    }
  }
  return (
    <form
      className="compact-form"
      onSubmit={save}
      aria-label="Prepare NatWest application"
    >
      <h3>Prepare signature review</h3>
      {m.externalSubmission && (
        <p role="status">
          An external application was submitted on{" "}
          {formatDateOnly(m.externalSubmission.submittedOn)}:{" "}
          {m.externalSubmission.stage}. Continue tracking a pending request in
          the account plan. A replacement needs a recorded rejection or
          withdrawal, its bank outcome PDF below and a reason for resubmission.
        </p>
      )}
      {sourceHash.current !== m.sourceHash && (
        <div role="status">
          <p>
            Building or document records changed. Your entries are retained.
            Review the updated details before requesting approvals.
          </p>
          <button
            type="button"
            onClick={() => {
              sourceHash.current = m.sourceHash;
              setChecks({});
              setDirty(true);
              setMapping((old) =>
                Object.fromEntries(
                  Object.entries(old).filter(([id]) =>
                    m.requirements.some(
                      (r) => r.id === id && r.required && !r.bankAccountId,
                    ),
                  ),
                ),
              );
            }}
          >
            Use updated details and review again
          </button>
        </div>
      )}
      <p>
        Building address:{" "}
        {m.buildingAddress || "Not recorded in building details"}
      </p>
      <p>
        Use the established NatWest mandate and the named signatories’ current
        details in Adobe. Keep personal details, bank identifiers and signature
        images out of application notes.
      </p>
      <p>
        Applicant: {m.applicant.name} · {m.applicant.companyNumber}
        <br />
        {m.applicant.address}
      </p>
      <p>
        Signatories:{" "}
        {m.identities
          .map(
            (i) => `${i.name} (${i.email ?? "staff account not configured"})`,
          )
          .join(" · ")}
      </p>
      <label>
        Retained blank form
        <select
          required
          value={formId}
          onChange={(e) => change(() => setFormId(e.target.value))}
        >
          <option value="">Download a fresh bank form first</option>
          {m.formDownloads
            .filter((f) => f.fresh)
            .map((f) => (
              <option key={f.id} value={f.id}>
                Downloaded {formatDateTime(f.downloadedAt)} ·{" "}
                {f.printedVersion ?? "Review updated form"}
              </option>
            ))}
        </select>
      </label>
      <p>
        Choose the completed PDF for each account you are requesting now. The
        same PDF may cover several accounts. Leave an account blank to apply for
        it later.
      </p>
      {m.requirements
        .filter((r) => r.required && !r.bankAccountId)
        .map((r) => (
          <label key={r.id}>
            {r.name}
            <select
              value={mapping[r.id] ?? ""}
              onChange={(e) =>
                change(() => setMapping({ ...mapping, [r.id]: e.target.value }))
              }
            >
              <option value="">Not included in this application</option>
              {mapping[r.id] &&
                !m.documents.some(
                  (d) =>
                    d.versionId === mapping[r.id] &&
                    ["Restricted", "Highly restricted"].includes(d.sensitivity),
                ) && (
                  <option disabled value={mapping[r.id]}>
                    Previously selected PDF needs a current Restricted
                    replacement
                  </option>
                )}
              {m.documents
                .filter((d) =>
                  ["Restricted", "Highly restricted"].includes(d.sensitivity),
                )
                .map((d) => (
                  <option key={d.versionId} value={d.versionId}>
                    {d.fileName}
                  </option>
                ))}
            </select>
          </label>
        ))}
      <label>
        NatWest customer reference
        <input
          value={reference}
          maxLength={100}
          onChange={(e) => change(() => setReference(e.target.value))}
        />
      </label>
      {Boolean(m.customerReference && reference !== m.customerReference) && (
        <label>
          Reason for changing the customer reference
          <textarea
            required
            minLength={3}
            maxLength={1000}
            value={referenceReason}
            onChange={(e) => change(() => setReferenceReason(e.target.value))}
          />
        </label>
      )}
      <details>
        <summary>Supporting PDFs</summary>
        {m.documents.map((d) => (
          <label key={d.versionId}>
            <input
              type="checkbox"
              checked={supporting.includes(d.versionId)}
              onChange={(e) =>
                change(() =>
                  setSupporting(
                    e.target.checked
                      ? [...supporting, d.versionId]
                      : supporting.filter((id) => id !== d.versionId),
                  ),
                )
              }
            />
            {d.fileName}
          </label>
        ))}
      </details>
      {m.packages.some((p) => p.sentAt) && (
        <>
          <label>
            Replacement for an earlier submission
            <select
              value={replace}
              onChange={(e) => change(() => setReplace(e.target.value))}
            >
              <option value="">New account request</option>
              {m.packages
                .filter((p) => p.sentAt)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    Application {p.versionNumber}
                  </option>
                ))}
            </select>
          </label>
        </>
      )}
      {(replace || m.externalSubmission) && (
        <label>
          Bank outcome and reason for resubmission
          <textarea
            required
            minLength={10}
            maxLength={2000}
            value={revision}
            onChange={(e) => change(() => setRevision(e.target.value))}
          />
        </label>
      )}
      <label>
        Application notes
        <textarea
          value={notes}
          maxLength={2000}
          onChange={(e) => change(() => setNotes(e.target.value))}
        />
      </label>
      {Object.entries(checksText).map(([key, text]) => (
        <label key={key}>
          <input
            required
            type="checkbox"
            checked={checks[key] ?? false}
            onChange={(e) =>
              change(() => setChecks({ ...checks, [key]: e.target.checked }))
            }
          />
          {text}
        </label>
      ))}
      <p>
        Both named signatories will review the exact completed files. Any later
        change requires a replacement and fresh approvals.
      </p>
      <button
        disabled={
          !Object.values(mapping).some(Boolean) ||
          Boolean(
            m.externalSubmission &&
            !["Rejected", "Withdrawn"].includes(m.externalSubmission.stage),
          ) ||
          sourceHash.current !== m.sourceHash ||
          m.identities.some((i) => !i.userId)
        }
      >
        Request signature approvals
      </button>
      <button
        className="secondary"
        type="button"
        onClick={() =>
          void (
            dirty
              ? confirmDiscard("this application preparation")
              : Promise.resolve(true)
          ).then((ok) => {
            if (ok) {
              setDirty(false);
              onClose();
            }
          })
        }
      >
        Discard preparation
      </button>
    </form>
  );
}
